import React from 'react';

interface ArchitecturalLinesProps {
  density?: 'low' | 'medium' | 'high';
  animate?: boolean;
  showCrosses?: boolean;
  className?: string;
}

const ArchitecturalLines: React.FC<ArchitecturalLinesProps> = ({ 
  density = 'medium', 
  animate = true, 
  showCrosses = true,
  className = '' 
}) => {
  // Calculate grid size based on density
  const gridSize = {
    low: 100,
    medium: 60,
    high: 40
  }[density];

  return (
    <div className={`fixed inset-0 pointer-events-none z-0 overflow-hidden ${className}`}>
      {/* Main vertical lines */}
      <div className="absolute inset-0">
        <div className="absolute left-16 top-0 w-px h-full bg-gradient-to-b from-transparent via-green-200/30 to-transparent"></div>
        <div className="absolute right-16 top-0 w-px h-full bg-gradient-to-b from-transparent via-green-200/30 to-transparent"></div>
        <div className="absolute left-1/4 top-0 w-px h-full bg-gradient-to-b from-transparent via-green-200/20 to-transparent"></div>
        <div className="absolute right-1/4 top-0 w-px h-full bg-gradient-to-b from-transparent via-green-200/20 to-transparent"></div>
      </div>

      {/* Horizontal connecting lines */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-16 right-16 h-px bg-gradient-to-r from-transparent via-green-200/30 to-transparent"></div>
        <div className="absolute top-1/2 left-16 right-16 h-px bg-gradient-to-r from-transparent via-green-200/30 to-transparent"></div>
        <div className="absolute top-3/4 left-16 right-16 h-px bg-gradient-to-r from-transparent via-green-200/30 to-transparent"></div>
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(80, 220, 160, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(80, 220, 160, 0.4) 1px, transparent 1px)
          `,
          backgroundSize: `${gridSize}px ${gridSize}px`
        }}
      ></div>

      {/* Corner crosses */}
      {showCrosses && (
        <>
          {/* Top corners */}
          <div className="absolute top-4 left-4 text-green-500/40 text-xl font-bold">+</div>
          <div className="absolute top-4 right-4 text-green-500/40 text-xl font-bold">+</div>
          
          {/* Bottom corners */}
          <div className="absolute bottom-4 left-4 text-green-500/40 text-xl font-bold">+</div>
          <div className="absolute bottom-4 right-4 text-green-500/40 text-xl font-bold">+</div>
          
          {/* Center intersection points */}
          <div className="absolute top-1/4 left-16 w-2 h-2 bg-green-500/60 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-1/4 right-16 w-2 h-2 bg-green-500/60 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-1/2 left-16 w-2 h-2 bg-green-500/60 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-1/2 right-16 w-2 h-2 bg-green-500/60 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-3/4 left-16 w-2 h-2 bg-green-500/60 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-3/4 right-16 w-2 h-2 bg-green-500/60 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
        </>
      )}

      {/* Animated elements */}
      {animate && (
        <>
          {/* Floating particles */}
          <div className="absolute top-1/3 left-1/3 w-1 h-1 bg-green-400/60 rounded-full animate-ping" style={{ animationDelay: '0s' }}></div>
          <div className="absolute top-2/3 right-1/3 w-1 h-1 bg-green-400/60 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-1/3 left-2/3 w-1 h-1 bg-green-400/60 rounded-full animate-ping" style={{ animationDelay: '2s' }}></div>
          
          {/* Scanning line effect */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-400/50 to-transparent animate-pulse" style={{ animationDuration: '4s' }}></div>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchitecturalLines; 